import React, { useMemo } from 'react';
import { HiArrowTrendingUp, HiArrowTrendingDown, HiArrowsRightLeft } from 'react-icons/hi2';
import { PriceChange } from '../../../../types';

interface TopChangesTableProps {
  data: PriceChange[];
}

const TopChangesTable: React.FC<TopChangesTableProps> = ({ data }) => {
  const formatCurrency = (value: number): string =>
    `$${Math.round(Math.abs(value)).toLocaleString('es-CL')}`;

  const formatPercentage = (oldPrice: number, newPrice: number): string => {
    if (oldPrice === 0) return '-';
    const pct = ((newPrice - oldPrice) / oldPrice) * 100;
    return `${pct >= 0 ? '+' : ''}${pct.toFixed(1)}%`;
  };

  const topIncreases = useMemo(() =>
    [...data].filter(c => c.new_price > c.old_price)
      .sort((a, b) => (b.new_price - b.old_price) - (a.new_price - a.old_price))
      .slice(0, 5),
  [data]);

  const topDecreases = useMemo(() =>
    [...data].filter(c => c.new_price < c.old_price)
      .sort((a, b) => (a.new_price - a.old_price) - (b.new_price - b.old_price))
      .slice(0, 5),
  [data]);

  const recentChanges = useMemo(() =>
    [...data].sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
      .slice(0, 5),
  [data]);

  const renderTable = (items: PriceChange[], emptyMessage: string) => {
    if (items.length === 0) {
      return (
        <div className="mp-empty-state" style={{ padding: '2rem 1rem' }}>
          <span className="mp-empty-state-title">{emptyMessage}</span>
        </div>
      );
    }

    return (
      <table className="mp-table">
        <thead className="mp-table-head">
          <tr>
            <th style={{ width: 50 }}>#</th>
            <th>Producto</th>
            <th>Anterior</th>
            <th>Nuevo</th>
            <th>Cambio</th>
          </tr>
        </thead>
        <tbody className="mp-table-body">
          {items.map((item, index) => {
            const change = item.new_price - item.old_price;
            const isIncrease = change > 0;
            const rankClass = index === 0 ? 'mp-rank-gold' : index === 1 ? 'mp-rank-silver' : index === 2 ? 'mp-rank-bronze' : '';

            return (
              <tr key={`${item.product_id}-${item.timestamp}-${index}`}>
                <td>
                  <span className={`mp-rank-badge ${rankClass}`}>{index + 1}</span>
                </td>
                <td style={{ fontWeight: 500 }}>{item.product_name}</td>
                <td style={{ color: 'var(--text-color-muted)' }}>{formatCurrency(item.old_price)}</td>
                <td>{formatCurrency(item.new_price)}</td>
                <td>
                  <span style={{ fontWeight: 600, color: isIncrease ? '#22c55e' : '#ef4444' }}>
                    {isIncrease ? '+' : ''}{formatCurrency(change)}
                    <span className="text-xs opacity-60 ml-1.5">
                      ({formatPercentage(item.old_price, item.new_price)})
                    </span>
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    );
  };

  const SectionCard = ({ icon, title, children }: { icon: React.ReactNode; title: string; children: React.ReactNode }) => (
    <div className="mp-table-card">
      <div className="flex items-center gap-3 px-5 py-3.5" style={{ borderBottom: '1px solid var(--border-color)' }}>
        {icon}
        <h5 className="text-base font-semibold m-0" style={{ color: 'var(--text-color)' }}>{title}</h5>
      </div>
      {children}
    </div>
  );

  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <SectionCard
          icon={<HiArrowTrendingUp size={20} style={{ color: '#22c55e' }} />}
          title="Top 5 Mayores Aumentos"
        >
          {renderTable(topIncreases, 'No hay aumentos de precio en este período')}
        </SectionCard>

        <SectionCard
          icon={<HiArrowTrendingDown size={20} style={{ color: '#ef4444' }} />}
          title="Top 5 Mayores Disminuciones"
        >
          {renderTable(topDecreases, 'No hay disminuciones de precio en este período')}
        </SectionCard>
      </div>

      <SectionCard
        icon={<HiArrowsRightLeft size={20} style={{ color: '#FC7556' }} />}
        title="Cambios Más Recientes"
      >
        {renderTable(recentChanges, 'No hay cambios de precio recientes')}
      </SectionCard>
    </div>
  );
};

export default TopChangesTable;
